import type { MetaDescriptor } from "react-router"

// Shared page metadata. React Router does NOT merge a route's `meta` export
// with its parents' — the deepest route that exports `meta` wins outright — so
// every route that sets a title also has to re-declare the Open Graph / Twitter
// tags or its social preview silently disappears. `pageMeta()` is the one place
// that whole block lives; each route calls it with just what differs.

export const SITE_URL = "https://pine59.com"
export const SITE_NAME = "Pine59"
export const SITE_TITLE = "Pine59: Aggregated location intelligence"
export const SITE_DESCRIPTION =
  "We transform aggregated location statistics into actionable insights about places and populations - powering analytics, products and AI."

/** The site-wide social card. Swapping this file swaps every page's preview. */
export const OG_IMAGE = `${SITE_URL}/og-image.png`

/**
 * The canonical absolute URL for a pathname: bare domain for the home page,
 * otherwise the path with any trailing slash stripped. Query strings are never
 * part of it, so `?from-post=…` style variants collapse onto the real page.
 * Used for both `<link rel="canonical">` (root.tsx) and `og:url`, so the two
 * can never disagree.
 */
export function canonicalUrl(pathname: string): string {
  return SITE_URL + (pathname === "/" ? "" : pathname.replace(/\/+$/, ""))
}

type PageMetaInput = {
  /** The <title>. Also the og:title / twitter:title unless `ogTitle` is set. */
  title: string
  /** Meta description + og/twitter description. Omit only for noindex pages. */
  description?: string
  /**
   * The current pathname, from the MetaFunction's `location` argument, so
   * og:url is derived from the real URL and never hand-typed. Omit for the
   * home page only.
   */
  pathname?: string
  /** og:type — "article" for blog posts, "website" otherwise. */
  ogType?: "website" | "article"
  /** Defaults to indexable; pass "noindex, nofollow" for transitional pages. */
  robots?: string
  /** Override the shared card for a specific page. Must be 1200x630, like
   *  the default — the og:image:width/height below are fixed. */
  image?: string
  /** Social-card title when it should differ from the <title> (blog posts). */
  ogTitle?: string
  /** Social-card blurb when it should differ from the meta description. */
  ogDescription?: string
  /** Page-specific tags appended verbatim (article:* times, JSON-LD, …). */
  extra?: MetaDescriptor[]
}

export function pageMeta({
  title,
  description,
  pathname = "/",
  ogType = "website",
  robots = "index, follow, max-image-preview:large",
  image = OG_IMAGE,
  ogTitle = title,
  ogDescription = description,
  extra = [],
}: PageMetaInput): MetaDescriptor[] {
  const url = canonicalUrl(pathname)
  const tags: MetaDescriptor[] = [
    { title },
    { name: "robots", content: robots },
    { name: "theme-color", content: "#00290F" },
    { property: "og:type", content: ogType },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: ogTitle },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:locale", content: "en_US" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: ogTitle },
    { name: "twitter:image", content: image },
  ]
  if (description) {
    tags.push({ name: "description", content: description })
  }
  if (ogDescription) {
    tags.push(
      { property: "og:description", content: ogDescription },
      { name: "twitter:description", content: ogDescription },
    )
  }
  return [...tags, ...extra]
}
